// src/utils/filterUtils.js
// ============================================================
// FILTER UTILITIES - Keg and beer filtering functions
// ============================================================
// Centralizes complex filtering logic that was duplicated
// in App.js (beersByLocation and onDeckBeersByLocation were nearly identical)
// ============================================================

import { normalizeString, isUnassigned, /* stringsMatch, */ matchesLocationFilter } from './stringUtils';

/**
 * Creates location/sublocation combinations from locations array
 * Used to group kegs by their location hierarchy
 * @param {Array} locations - Array of location objects with optional sublocations
 * @returns {Array} Flattened array of location combinations
 */
export const createLocationCombinations = (locations) => {
  const combos = [];
  
  locations.forEach(loc => {
    if (loc.sublocations && loc.sublocations.length > 0) {
      loc.sublocations.forEach(sub => {
        combos.push({
          locationId: loc.id,
          location: loc.name,
          sublocation: sub.name,
          sublocationId: sub.id
        });
      });
    } else {
      combos.push({
        locationId: loc.id,
        location: loc.name,
        sublocation: null,
        sublocationId: null
      });
    }
  });
  
  // Add fallback for unassigned kegs
  combos.push({
    locationId: null,
    location: 'Unassigned',
    sublocation: null,
    sublocationId: null
  });
  
  return combos;
};

/**
 * Checks if a keg's location matches a location combination
 * Handles the 'Unassigned' special case
 * @param {Object} keg - Keg object with location and sublocation
 * @param {Object} combo - Location combination to match
 * @returns {boolean} True if keg location matches combination
 */
const kegMatchesLocationCombo = (keg, combo) => {
  const kegLoc = normalizeString(keg.location);
  const comboLoc = normalizeString(combo.location);
  const kegSub = normalizeString(keg.sublocation);
  const comboSub = normalizeString(combo.sublocation);
  
  // Location matching
  const locationMatches = comboLoc === 'unassigned'
    ? isUnassigned(keg.location)
    : kegLoc === comboLoc;
  
  // Sublocation matching
  const sublocationMatches = combo.sublocation === null
    ? (keg.sublocation === null || keg.sublocation === '')
    : kegSub === comboSub;
  
  return locationMatches && sublocationMatches;
};

/**
 * Filters kegs that are currently on tap, grouped by location
 * @param {Array} locationCombos - Array of location combinations
 * @param {Array} kegs - Array of keg objects
 * @returns {Array} Location groups with filtered beers
 */
export const filterBeersOnTap = (locationCombos, kegs) => {
  return locationCombos.map(combo => ({
    ...combo,
    beers: kegs.filter(keg => {
      if (!keg.currentBeer || keg.isEmpty) return false;
      
      const status = normalizeString(keg.tapStatus);
      const isOnTap = status === 'on tap';
      
      return kegMatchesLocationCombo(keg, combo) && isOnTap;
    })
  })).filter(group => group.beers.length > 0);
};

/**
 * Filters kegs that are on deck (conditioning, lagering, carbonating, on deck)
 * @param {Array} locationCombos - Array of location combinations
 * @param {Array} kegs - Array of keg objects
 * @returns {Array} Location groups with filtered beers
 */
export const filterBeersOnDeck = (locationCombos, kegs) => {
  const onDeckStatuses = ['conditioning', 'lagering', 'carbonating', 'on deck'];
  
  return locationCombos.map(combo => ({
    ...combo,
    beers: kegs.filter(keg => {
      if (!keg.currentBeer || keg.isEmpty) return false;
      
      const status = normalizeString(keg.tapStatus);
      const isOnDeck = onDeckStatuses.includes(status);
      
      return kegMatchesLocationCombo(keg, combo) && isOnDeck;
    })
  })).filter(group => group.beers.length > 0);
};

/**
 * Main keg filter function for dashboard
 * Applies search, tap status, clean status, location, and quick filters
 * @param {Array} kegs - Array of keg objects to filter
 * @param {Object} filters - Filter configuration object
 * @returns {Array} Filtered kegs array
 */
export const filterKegs = (kegs, filters) => {
  const {
    searchTerm = '',
    filterTapStatus = [],
    filterCleanStatus = [],
    filterLocation = 'all',
    quickFilter = 'all'
  } = filters;
  
  return kegs.filter(keg => {
    // Search filter (name or keg number)
    const matchesSearch = !searchTerm || 
      keg.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      keg.kegNumber.toString().includes(searchTerm);
    
    // Tap status filter
    const status = normalizeString(keg.tapStatus);
    const matchesTap = filterTapStatus.length === 0 || 
      filterTapStatus.map(s => normalizeString(s)).includes(status);
    
    // Cleaning status filter
    const cleanStatus = normalizeString(keg.cleaningStatus);
    const matchesClean = filterCleanStatus.length === 0 || 
      filterCleanStatus.map(s => normalizeString(s)).includes(cleanStatus);
    
    // Location filter
    const matchesLocation = matchesLocationFilter(keg.location, filterLocation);
    
    // Quick filter (all, empty, filled)
    const matchesQuickFilter = 
      quickFilter === 'all' ? true :
      quickFilter === 'empty' ? keg.isEmpty :
      quickFilter === 'filled' ? !keg.isEmpty :
      true;
    
    return matchesSearch && matchesTap && matchesClean && matchesLocation && matchesQuickFilter;
  });
};
/**
 * Sorts beers by tap order defined in location/sublocation settings
 * Falls back to numeric tap number if custom order not found
 * @param {Array} beers - Array of keg objects with currentBeer
 * @param {Array} locations - Array of location objects with tapNames
 * @param {string} locationName - The location name to find tap order for
 * @param {string} sublocationName - The sublocation name (optional)
 * @returns {Array} Sorted array of beers
 */
export const sortBeersByTapOrder = (beers, locations, locationName, sublocationName) => {
  if (!beers || beers.length === 0) return beers;
  
  // Find the location and get tap order
  const location = locations.find(loc => loc.name === locationName);
  if (!location) return beers;
  
  let tapOrder = [];
  
  // Get tap names from sublocation or location
  if (sublocationName && location.sublocations?.length > 0) {
    const sublocation = location.sublocations.find(sub => sub.name === sublocationName);
    tapOrder = sublocation?.tapNames || [];
  } else {
    tapOrder = location.tapNames || [];
  }
  
  // If no tap order defined, sort by numeric tap number
  if (tapOrder.length === 0) {
    return [...beers].sort((a, b) => {
      const ta = a.tapNumber ? parseInt(a.tapNumber) : Infinity;
      const tb = b.tapNumber ? parseInt(b.tapNumber) : Infinity;
      if (ta !== tb) return ta - tb;
      return (a.kegNumber || 0) - (b.kegNumber || 0);
    });
  }
  
  // Sort by tap order defined in location/sublocation
  // tapNumber is 1-based, so we use it directly as the position in tapOrder array
  return [...beers].sort((a, b) => {
    const aTapNum = a.tapNumber ? parseInt(a.tapNumber) : null;
    const bTapNum = b.tapNumber ? parseInt(b.tapNumber) : null;
    
    // If both have valid tap numbers within the tapOrder array, compare positions
    if (aTapNum && bTapNum && aTapNum <= tapOrder.length && bTapNum <= tapOrder.length) {
      return aTapNum - bTapNum;
    }
    
    // If only one has a valid tap number, it comes first
    if (aTapNum && aTapNum <= tapOrder.length) return -1;
    if (bTapNum && bTapNum <= tapOrder.length) return 1;
    
    // If neither is in tap order, sort by numeric tap number
    const ta = a.tapNumber ? parseInt(a.tapNumber) : Infinity;
    const tb = b.tapNumber ? parseInt(b.tapNumber) : Infinity;
    if (ta !== tb) return ta - tb;
    return (a.kegNumber || 0) - (b.kegNumber || 0);
  });
};